import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import MapDialog from './MapDialog';

const MapDialogContext = createContext(null);

function normalizeOptions(options) {
  if (typeof options === 'string') {
    return { title: options };
  }
  return options || {};
}

export function MapDialogProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const resolverRef = useRef(null);

  const settle = useCallback((result) => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setDialog(null);
    resolver?.(result);
  }, []);

  const openDialog = useCallback((nextDialog, resolve) => {
    if (resolverRef.current) {
      resolverRef.current(false);
    }
    resolverRef.current = resolve;
    setDialog(nextDialog);
  }, []);

  const showDialog = useCallback((options) => new Promise((resolve) => {
    openDialog({
      ...normalizeOptions(options),
      mode: 'dialog',
    }, resolve);
  }), [openDialog]);

  const showConfirm = useCallback((options) => new Promise((resolve) => {
    openDialog({
      ...normalizeOptions(options),
      mode: 'confirm',
    }, resolve);
  }), [openDialog]);

  const value = useMemo(() => ({
    showDialog,
    showConfirm,
  }), [showDialog, showConfirm]);

  return (
    <MapDialogContext.Provider value={value}>
      {children}
      <MapDialog
        open={Boolean(dialog)}
        kicker={dialog?.kicker}
        title={dialog?.title}
        body={dialog?.body}
        accentColor={dialog?.accentColor}
        primaryLabel={dialog?.primaryLabel}
        cancelLabel={dialog?.cancelLabel}
        loading={dialog?.loading}
        onClose={() => settle(dialog?.mode === 'confirm' ? false : true)}
        onConfirm={dialog?.mode === 'confirm' ? () => settle(true) : undefined}
      />
    </MapDialogContext.Provider>
  );
}

export function useMapDialog() {
  const context = useContext(MapDialogContext);
  if (!context) {
    throw new Error('useMapDialog must be used within MapDialogProvider');
  }
  return context;
}

export default useMapDialog;
